/* eslint-disable consistent-return */
/* eslint-disable i18next/no-literal-string */
import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import classes from './styles.module.scss';

import { buildRapidStorOptions } from '@/core/utils/application';

import { UNIT_FILTER_SAMPLE } from './unitFilterSample';

// Simple runtime editor for window.rapidstor_settings.app_config (dev only)
// Opens with SHIFT + 6 (Digit6 + shiftKey) when dev flag is true

const STORAGE_KEY = 'rapidstor-dev-config-draft';
const OVERRIDE_KEY = 'rapidstor-dev-app-config-override';

interface Props {
    onForceRerender: () => void;
}

export default function DevConfigModal({ onForceRerender }: Props) {
    const [open, setOpen] = useState(false);
    const [jsonValue, setJsonValue] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [savedAt, setSavedAt] = useState<Date | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const loadInitial = useCallback(() => {
        try {
            const override = localStorage.getItem(OVERRIDE_KEY);
            if (override) {
                setJsonValue(override);
                return;
            }
            const current = window.rapidstor_settings?.app_config || {};
            setJsonValue(JSON.stringify(current, null, 2));
        } catch (e) {
            // ignore
        }
    }, []);

    const toggleOpen = useCallback(() => {
        setOpen((prev) => {
            const next = !prev;
            if (next) {
                // Load latest each open
                loadInitial();
                setTimeout(() => textareaRef.current?.focus(), 30);
            }
            return next;
        });
    }, [loadInitial]);

    // Keyboard shortcut SHIFT+6 (Digit6 with shift)
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.code === 'Digit6' && e.shiftKey) {
                e.preventDefault();
                toggleOpen();
            }
            if (e.key === 'Escape' && open) {
                setOpen(false);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, toggleOpen]);

    // Persist draft locally (helps during accidental closes)
    useEffect(() => {
        if (!open) return;
        const id = setTimeout(() => {
            try {
                localStorage.setItem(STORAGE_KEY, jsonValue);
            } catch {
                /* noop */
            }
        }, 400);
        return () => clearTimeout(id);
    }, [jsonValue, open]);

    // Attempt to restore draft when first opened
    useEffect(() => {
        if (open) {
            try {
                const draft = localStorage.getItem(STORAGE_KEY);
                if (draft) setJsonValue(draft);
            } catch {
                /* noop */
            }
        }
    }, [open]);

    const handleReset = () => {
        loadInitial();
        setError(null);
    };

    // Fills the editor only; Save applies it.
    const handleInsertUnitFilterSample = () => {
        setError(null);
        try {
            const parsed = JSON.parse(jsonValue);
            parsed.journey = { ...parsed.journey, ...UNIT_FILTER_SAMPLE };
            setJsonValue(JSON.stringify(parsed, null, 2));
        } catch (e) {
            setError((e as Error).message);
        }
    };

    const handleSave = () => {
        setError(null);
        try {
            const parsed = JSON.parse(jsonValue);
            if (typeof parsed !== 'object' || Array.isArray(parsed)) {
                throw new Error('Root must be an object.');
            }
            // Persist override to localStorage so it survives reloads
            localStorage.setItem(OVERRIDE_KEY, JSON.stringify(parsed, null, 2));

            // Replace app_config entirely (dev behaviour)
            window.rapidstor_settings.app_config = parsed;
            // Rebuild rapidstor options from new app_config while attempting to preserve runtime tokens
            const root = document.getElementById('rapidstor-v2-frontend');
            const personaAttr = root?.getAttribute('data-persona');
            const moveInUrlAttr = root?.getAttribute('data-moveInUrl');
            const prevLocation = window.rapidstor_settings.rapidstor_options.location;
            const prevLocationToken = window.rapidstor_settings.rapidstor_options.locationToken;
            window.rapidstor_settings.rapidstor_options = buildRapidStorOptions(
                parsed,
                personaAttr,
                moveInUrlAttr,
            );
            // Restore runtime-populated values if still applicable
            if (prevLocation) {
                window.rapidstor_settings.rapidstor_options.location = prevLocation;
            }
            if (prevLocationToken) {
                window.rapidstor_settings.rapidstor_options.locationToken = prevLocationToken;
            }

            setSavedAt(new Date());
            onForceRerender();
        } catch (e) {
            setError((e as Error).message);
        }
    };

    const handleClearOverride = () => {
        try {
            localStorage.removeItem(OVERRIDE_KEY);
            loadInitial();
            // Reload the original attribute-based config without full page reload
            const root = document.getElementById('rapidstor-v2-frontend');
            const attrConfig = root?.getAttribute('data-appConfig');
            if (attrConfig) {
                const parsed = JSON.parse(attrConfig);
                window.rapidstor_settings.app_config = parsed;
                const personaAttr = root?.getAttribute('data-persona');
                const moveInUrlAttr = root?.getAttribute('data-moveInUrl');
                window.rapidstor_settings.rapidstor_options = buildRapidStorOptions(
                    parsed,
                    personaAttr,
                    moveInUrlAttr,
                );
                onForceRerender();
            }
        } catch (e) {
            setError((e as Error).message);
        }
    };

    const isOverrideActive = useMemo(() => localStorage.getItem(OVERRIDE_KEY) !== null, []);

    if (!window.rapidstor_settings?.rapidstor_options?.dev) return null;
    if (!open) return null;

    return (
        <div className={classes.overlay}>

            {isOverrideActive && (
                <div className={classes.overrideActive}>
                    <p>Override active</p>
                </div>
            )}
            <div
                className={classes.modal}
                role="dialog"
                aria-modal="true"
                aria-label="RapidStor Dev Config"
            >
                <header className={classes.header}>
                    <div className={classes.title}>RapidStor Dev Config</div>
                    <div className={classes.meta}>
                        <span>Press ESC to close. SHIFT+6 toggles.</span>
                        {savedAt && (
                            <span className={classes.saved}>
                                Saved
                                {' '}
                                {savedAt.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                </header>
                <div className={classes.body}>
                    <label className={classes.label}>
                        <span>app_config override (editable JSON)</span>
                        <textarea
                            ref={textareaRef}
                            className={classes.textarea}
                            value={jsonValue}
                            onChange={(e) => setJsonValue(e.target.value)}
                            spellCheck={false}
                        />
                    </label>
                    {error && (
                        <p className={classes.error}>
                            Error:
                            {error}
                        </p>
                    )}
                </div>
                <footer className={classes.footer}>
                    <button
                        type="button"
                        className={classes.btn}
                        onClick={handleSave}
                    >
                        Save
                    </button>
                    <button
                        type="button"
                        className={classes.btn}
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        className={classes.btn}
                        onClick={handleInsertUnitFilterSample}
                    >
                        Unit filter sample
                    </button>
                    <button
                        type="button"
                        className={classes.btn}
                        onClick={handleClearOverride}
                    >
                        Clear Override
                    </button>
                    <button
                        type="button"
                        className={classes.btn}
                        onClick={() => setOpen(false)}
                    >
                        Close
                    </button>
                </footer>
            </div>
        </div>
    );
}
